import * as React from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

/**
 * Compact date picker that matches the app's shadcn look-and-feel.
 * Renders an outline button with the SA-format date (dd MMM yyyy) and opens
 * a shadcn Calendar popover.
 *
 * Props:
 *  - value: ISO string ("YYYY-MM-DD") or null
 *  - onChange: (newIsoString | null) => void   // null clears
 *  - placeholder: text shown when value is empty (default "Pick a date")
 *  - disabled: boolean
 *  - clearable: boolean (default true)
 *  - className: extra classes for the trigger button
 *  - triggerTestId: data-testid override for the trigger
 */
export function DatePickerField({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled = false,
  clearable = true,
  className,
  triggerTestId,
  buttonSize = 'sm',
}) {
  const [open, setOpen] = React.useState(false);

  const parsed = React.useMemo(() => {
    if (!value) return undefined;
    try {
      return parseISO(value);
    } catch {
      return undefined;
    }
  }, [value]);

  const label = parsed ? format(parsed, 'dd MMM yyyy') : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={buttonSize}
          disabled={disabled}
          data-testid={triggerTestId}
          className={cn(
            'justify-start text-left font-normal gap-2',
            !parsed && 'text-slate-400',
            className,
          )}
        >
          <CalendarIcon className="w-4 h-4 shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {clearable && parsed && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange?.(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange?.(null);
                }
              }}
              className="text-slate-400 hover:text-slate-700 inline-flex"
              aria-label="Clear date"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={(d) => {
            onChange?.(d ? format(d, 'yyyy-MM-dd') : null);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePickerField;
